import type { ColumnPrediction } from '@features/regional-overview/digital-traps/domain/flight-prediction.models';
import type { FlightRealizationEnum } from '@features/regional-overview/digital-traps/domain/flight-stage.enum';
import { FLIGHT_STAGE_TIMELINE } from '@features/regional-overview/digital-traps/domain/tests-id.constants';
import { STTypo, STTypoModels } from '@protector/components';
import { useMediaQuery } from 'beautiful-react-hooks';
import { COLOR_OPTIONS } from 'config/style';
import { MEDIA_QUERIES } from 'config/style/breakpoints';
import FlightStageTag from '../flight-stage-tag/flight-stage-tag';
import { buildSegments } from './flight-stage-timeline.utils';
import styles from './flight-stage-timeline.module.css';

const NO_PREDICTION_PLACEHOLDER = '--';

export interface FlightStageTimelineProps {
  dates: string[];
  columnPredictions: (ColumnPrediction | undefined)[];
}

export const FlightStageTimeline = ({ dates, columnPredictions }: FlightStageTimelineProps) => {
  const isMobile = useMediaQuery(MEDIA_QUERIES.MOBILE);
  const dateTypoType = isMobile ? STTypoModels.TypeOptions.C2 : STTypoModels.TypeOptions.P2;
  const segments = buildSegments(columnPredictions);
  const hasPredictions = segments.length > 0;
  const gridTemplateColumns = `repeat(${dates.length}, 1fr)`;

  return (
    <div className={styles.timeline} data-testid={FLIGHT_STAGE_TIMELINE}>
      <div className={styles.dates} style={{ gridTemplateColumns }}>
        {dates.map(date => (
          <STTypo
            key={date}
            customClassName={styles.date}
            type={dateTypoType}
            fontFamily='Noto Sans'
            color={COLOR_OPTIONS.NEUTRAL_100}
            lineHeight='20px'
            align='center'>
            {date}
          </STTypo>
        ))}
      </div>
      <div className={styles.stages} style={{ gridTemplateColumns }}>
        {hasPredictions
          ? segments.map(({ prediction, start, span }) => (
              <div key={`stage-${start}`} className={styles.segment} style={{ gridColumn: `${start + 1} / span ${span}` }}>
                <FlightStageTag flightGeneration={prediction.generation} flightStage={prediction.stage as FlightRealizationEnum} />
              </div>
            ))
          : dates.map((date, index) => (
              <STTypo
                key={`empty-${date}-${index}`}
                customClassName={styles.emptyCell}
                type={STTypoModels.TypeOptions.C1}
                fontFamily='Noto Sans'
                color={COLOR_OPTIONS.NEUTRAL_60}
                lineHeight='20px'
                align='center'>
                {NO_PREDICTION_PLACEHOLDER}
              </STTypo>
            ))}
      </div>
    </div>
  );
};
